import { PlusOutlined, ReloadOutlined, DeleteOutlined, CopyOutlined, KeyOutlined } from '@ant-design/icons';
import { Button, Col, Form, Input, Modal, Row, Table, message, Popconfirm, Space, Typography, Card } from 'antd';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useState } from 'react';

import { elementWithLoading } from '../../../components/LoadingWrapper';
import { tokensService } from '../../../services/tokens';
import { TokenR, TokenManagementModalProps } from '../../../types/types';
import { copyToClipboard } from '../../../utils/clipboard';
import { openSuccessNotification } from '../../../utils/notifications';
import { showApiErrors } from '../../../utils/showApiErrors';
import styles from './TokenManagementModal.module.css';

const { Text } = Typography;

export const TokenManagementModal = ({
  modalVisible,
  userId,
  userName,
  onCancel
}: TokenManagementModalProps): JSX.Element => {
  const [form] = Form.useForm();
  const [tokens, setTokens] = useState<TokenR[]>([]);
  const [loading, setLoading] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [newToken, setNewToken] = useState<TokenR | null>(null);

  useEffect(() => {
    const loadTokens = async () => {
      setLoading(true);
      try {
        const userTokens = await tokensService.getUserTokens(userId);
        setTokens(userTokens);
      } catch (e) {
        showApiErrors(e as AxiosError);
      } finally {
        setLoading(false);
      }
    };

    if (modalVisible) {
      loadTokens();
    }
  }, [modalVisible, userId]);

  const handleCreateToken = useCallback(
    async (values: { description: string }) => {
      setCreatingToken(true);
      try {
        const token = await tokensService.createToken({
          description: values.description,
          userId
        });
        setNewToken(token);
        setTokens((prev) => [token, ...prev]);
        form.resetFields();
        openSuccessNotification({
          message: 'Token created successfully!',
          description: 'The token has been copied to your clipboard.',
          duration: 8
        });

        await copyToClipboard(token.token);
      } catch (e) {
        showApiErrors(e as AxiosError);
      } finally {
        setCreatingToken(false);
      }
    },
    [userId, form]
  );

  const handleRegenerateToken = useCallback(
    async (tokenId: number) => {
      try {
        const regeneratedToken = await tokensService.regenerateToken(tokenId, userId);
        setTokens((prev) =>
          prev.map((t) => {
            return t.id === tokenId ? regeneratedToken : t;
          })
        );
        setNewToken(regeneratedToken);
        openSuccessNotification({
          message: 'Token regenerated successfully!',
          description: 'The new token has been copied to your clipboard.',
          duration: 8
        });

        await copyToClipboard(regeneratedToken.token);
      } catch (e) {
        showApiErrors(e as AxiosError);
      }
    },
    [userId]
  );

  const handleDeleteToken = useCallback(
    async (tokenId: number) => {
      try {
        await tokensService.deleteToken(tokenId, userId);
        setTokens((prev) => prev.filter((t) => t.id !== tokenId));
        openSuccessNotification({
          message: 'Token deleted successfully!',
          duration: 8
        });
      } catch (e) {
        showApiErrors(e as AxiosError);
      }
    },
    [userId]
  );

  const copyToClipboardWithMessage = useCallback(async (text: string) => {
    const success = await copyToClipboard(text);
    if (success) {
      message.success('Copied to clipboard!');
    } else {
      message.error('Failed to copy to clipboard');
    }
  }, []);

  const columns = [
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 200,
      render: (text: string) => (
        <div className={styles.descriptionCell}>
          <Text strong>{text}</Text>
        </div>
      )
    },
    {
      title: 'Date Created',
      dataIndex: 'createdDatetime',
      key: 'createdDatetime',
      width: 150,
      render: (date: string) => <div className={styles.dateCell}>{dayjs(date).format('YYYY-MM-DD HH:mm:ss')}</div>
    },
    {
      title: 'Token',
      key: 'token',
      width: 300,
      render: (_: any, record: TokenR) => {
        const isNewToken = newToken?.id === record.id;
        return (
          <div className={styles.tokenCell}>
            <Space>
              <Text code className={styles.tokenText}>
                {isNewToken ? record.token : '••••••••••••••••••••••••••••••••'}
              </Text>
              {isNewToken && (
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => void copyToClipboardWithMessage(record.token)}
                />
              )}
            </Space>
          </div>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 50,
      render: (_: any, record: TokenR) => (
        <div className={styles.actionsCell}>
          <Space>
            <Popconfirm
              title="This will replace the current token with a new one. Are you sure?"
              onConfirm={() => void handleRegenerateToken(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" size="small" icon={<ReloadOutlined />} title="Regenerate Token" />
            </Popconfirm>
            <Popconfirm
              title="This action cannot be undone. Are you sure?"
              onConfirm={() => void handleDeleteToken(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="text" size="small" danger icon={<DeleteOutlined />} title="Delete Token" />
            </Popconfirm>
          </Space>
        </div>
      )
    }
  ];

  const modalContent = (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={
              <Space align="center">
                <KeyOutlined className={styles.centeredIcon} />
                <span>Create New Token</span>
              </Space>
            }
            size="small"
          >
            <Form form={form} layout="vertical" onFinish={(values) => void handleCreateToken(values)}>
              <Row gutter={16} align="bottom">
                <Col span={16}>
                  <Form.Item
                    name="description"
                    label="Description"
                    rules={[{ required: true, message: 'Please enter a description' }]}
                  >
                    <Input
                      placeholder="Enter token description (e.g., API access for mobile app)"
                      disabled={creatingToken}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      htmlType="submit"
                      loading={creatingToken}
                      disabled={creatingToken}
                      className={styles.createTokenButton}
                    >
                      Create Token
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>

        <Col span={24}>
          <Card
            title={
              <Space align="center">
                <KeyOutlined className={styles.centeredIcon} />
                <span>User Tokens</span>
              </Space>
            }
            size="small"
          >
            <Table
              dataSource={tokens}
              columns={columns}
              rowKey="id"
              pagination={false}
              loading={loading}
              size="small"
              locale={{
                emptyText: 'No tokens found. Create your first token above.'
              }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );

  return (
    <Modal
      title={
        <Space align="center">
          <KeyOutlined className={styles.centeredIcon} />
          <span>Manage Tokens for {userName}</span>
        </Space>
      }
      open={modalVisible}
      onCancel={onCancel}
      footer={null}
      width="80%"
    >
      {elementWithLoading(modalContent, loading)}
    </Modal>
  );
};
